// Handler factory for income type and record CRUD — mirrors useExpenseHandlers
// but covers only workspace-level income (no transaction-level income records).

import * as workspaceService from '../services/workspaceService';
import { resolveExpenseSelection, validateExpenseNode, planExpenseNodeMove } from '../utils/expenseTaxonomy';

const normalizeDateOnlyValue = (value) => {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : '';
};

export function useIncomeHandlers({
    selectedWorkspaceId,
    user,
    incomeTypes,
    transactions,
    showToast,
}) {
    const handleCreateIncomeType = async (nameOrPayload, parentCategoryId = null) => {
        if (!selectedWorkspaceId) return null;
        if (!user) {
            showToast('Sign in before saving.', 'error');
            return null;
        }
        const input = typeof nameOrPayload === 'string'
            ? { name: nameOrPayload, parentCategoryId }
            : (nameOrPayload || {});
        const validation = validateExpenseNode({
            name: input.name,
            parentCategoryId: input.parentCategoryId || null
        }, incomeTypes);
        if (!validation.valid) {
            showToast(validation.message, 'error');
            return null;
        }
        try {
            const timestamp = Date.now();
            const payload = {
                name: validation.normalizedName,
                parentCategoryId: validation.normalizedParentCategoryId,
                createdAt: timestamp,
                updatedAt: timestamp,
                createdBy: user.uid,
                updatedBy: user.uid
            };
            const docRef = await workspaceService.createIncomeType(selectedWorkspaceId, payload);
            showToast(validation.normalizedParentCategoryId ? 'Income subcategory saved.' : 'Income category saved.');
            return { id: docRef.id, ...payload };
        } catch (error) {
            console.error('Error creating income type:', error);
            showToast(`Failed to save income category: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleUpdateIncomeType = async (typeId, updates) => {
        if (!selectedWorkspaceId || !typeId) return;
        if (!user) {
            showToast('Sign in before updating.', 'error');
            return;
        }
        const nextValues = typeof updates === 'string' ? { name: updates } : (updates || {});
        const existingType = incomeTypes.find(type => type.id === typeId);
        const validation = validateExpenseNode({
            id: typeId,
            name: nextValues.name,
            parentCategoryId: nextValues.parentCategoryId ?? existingType?.parentCategoryId ?? null
        }, incomeTypes);
        if (!validation.valid) {
            showToast(validation.message, 'error');
            return;
        }
        try {
            await workspaceService.updateIncomeType(selectedWorkspaceId, typeId, {
                name: validation.normalizedName,
                parentCategoryId: validation.normalizedParentCategoryId,
                updatedAt: Date.now(),
                updatedBy: user.uid
            });
            showToast('Income category updated.');
        } catch (error) {
            console.error('Error updating income type:', error);
            showToast(`Failed to update income category: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleMoveIncomeType = async (typeId, targetParentCategoryId = null, options = {}) => {
        if (!selectedWorkspaceId || !typeId) return;
        if (!user) {
            showToast('Sign in before updating.', 'error');
            return;
        }
        const plan = planExpenseNodeMove({
            nodeId: typeId,
            targetParentCategoryId,
            childCategoryMode: options.childCategoryMode || 'reparent'
        }, incomeTypes);
        if (!plan.valid) {
            showToast(plan.message || 'Unable to move income category.', 'error');
            return;
        }
        try {
            await workspaceService.batchUpdateIncomeTypes(selectedWorkspaceId, plan.updates, user.uid);
            showToast(plan.isPromotion ? 'Income subcategory promoted.' : plan.isDemotion ? 'Income category demoted.' : 'Income category moved.');
        } catch (error) {
            console.error('Error moving income type:', error);
            showToast(`Failed to move income category: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleDeleteIncomeType = async (typeId) => {
        if (!selectedWorkspaceId || !typeId) return;
        try {
            await workspaceService.deleteIncomeType(selectedWorkspaceId, typeId);
            showToast('Income category deleted.');
        } catch (error) {
            console.error('Error deleting income type:', error);
            showToast(`Failed to delete income category: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleCreateWorkspaceIncome = async (incomeData = {}) => {
        if (!selectedWorkspaceId) return;
        if (!user) {
            showToast('Sign in before adding income.', 'error');
            return;
        }
        const normalizedDate = normalizeDateOnlyValue(incomeData.date);
        if (!normalizedDate) {
            showToast('Provide a valid income date.', 'error');
            return;
        }
        const rawAmount = Number(incomeData.amount);
        if (!Number.isFinite(rawAmount) || rawAmount === 0) {
            showToast('Enter a valid income amount.', 'error');
            return;
        }
        const normalizedAmount = Math.round(rawAmount * 100) / 100;
        const resolvedSelection = resolveExpenseSelection(incomeData, incomeTypes);
        if (!resolvedSelection.categoryName) {
            showToast('Select or enter an income category.', 'error');
            return;
        }
        const name = (incomeData.name || '').trim();
        if (!name) {
            showToast('Income description is required.', 'error');
            return;
        }
        const transactionId = (incomeData.transactionId || '').trim();
        const linkedTransaction = transactionId ? transactions.find(t => t?.id === transactionId) : null;
        const resolvedStatus = (linkedTransaction?.status || '').trim() || null;
        const notes = (incomeData.notes || '').trim();
        const timestamp = Date.now();
        const payload = {
            name,
            amount: normalizedAmount,
            status: resolvedStatus,
            date: normalizedDate,
            categoryId: resolvedSelection.categoryId,
            categoryName: resolvedSelection.categoryName,
            subcategoryId: resolvedSelection.subcategoryId,
            subcategoryName: resolvedSelection.subcategoryName || null,
            typeId: resolvedSelection.typeId,
            typeName: resolvedSelection.typeName || resolvedSelection.categoryName,
            customCategoryName: resolvedSelection.categoryId ? null : resolvedSelection.categoryName,
            customTypeName: resolvedSelection.categoryId ? null : resolvedSelection.categoryName,
            transactionId: transactionId || null,
            notes: notes || null,
            createdAt: timestamp,
            createdBy: user.uid,
            updatedAt: timestamp,
            updatedBy: user.uid
        };
        try {
            await workspaceService.createWorkspaceIncome(selectedWorkspaceId, payload);
            showToast('Income recorded.');
        } catch (error) {
            console.error('Error recording workspace income:', error);
            showToast(`Failed to record income: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleUpdateWorkspaceIncome = async (incomeId, incomeData = {}) => {
        if (!selectedWorkspaceId || !incomeId) return;
        if (!user) {
            showToast('Sign in before editing income.', 'error');
            return;
        }
        const normalizedDate = normalizeDateOnlyValue(incomeData.date);
        if (!normalizedDate) {
            showToast('Provide a valid income date.', 'error');
            return;
        }
        const rawAmount = Number(incomeData.amount);
        if (!Number.isFinite(rawAmount) || rawAmount === 0) {
            showToast('Enter a valid income amount.', 'error');
            return;
        }
        const normalizedAmount = Math.round(rawAmount * 100) / 100;
        const resolvedSelection = resolveExpenseSelection(incomeData, incomeTypes);
        if (!resolvedSelection.categoryName) {
            showToast('Select or enter an income category.', 'error');
            return;
        }
        const name = (incomeData.name || '').trim();
        if (!name) {
            showToast('Income description is required.', 'error');
            return;
        }
        const transactionId = (incomeData.transactionId || '').trim();
        const linkedTransaction = transactionId ? transactions.find(t => t?.id === transactionId) : null;
        const resolvedStatus = (linkedTransaction?.status || '').trim() || null;
        const notes = (incomeData.notes || '').trim();
        const payload = {
            name,
            amount: normalizedAmount,
            status: resolvedStatus,
            date: normalizedDate,
            categoryId: resolvedSelection.categoryId,
            categoryName: resolvedSelection.categoryName,
            subcategoryId: resolvedSelection.subcategoryId,
            subcategoryName: resolvedSelection.subcategoryName || null,
            typeId: resolvedSelection.typeId,
            typeName: resolvedSelection.typeName || resolvedSelection.categoryName,
            customCategoryName: resolvedSelection.categoryId ? null : resolvedSelection.categoryName,
            customTypeName: resolvedSelection.categoryId ? null : resolvedSelection.categoryName,
            transactionId: transactionId || null,
            notes: notes || null,
            updatedAt: Date.now(),
            updatedBy: user.uid
        };
        try {
            await workspaceService.updateWorkspaceIncome(selectedWorkspaceId, incomeId, payload);
            showToast('Income updated.');
        } catch (error) {
            console.error('Error updating workspace income:', error);
            showToast(`Failed to update income: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleDeleteWorkspaceIncome = async (incomeId) => {
        if (!selectedWorkspaceId || !incomeId) return;
        try {
            await workspaceService.deleteWorkspaceIncome(selectedWorkspaceId, incomeId);
            showToast('Income removed.');
        } catch (error) {
            console.error('Error deleting workspace income:', error);
            showToast(`Failed to delete income: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    return {
        handleCreateIncomeType,
        handleUpdateIncomeType,
        handleMoveIncomeType,
        handleDeleteIncomeType,
        handleCreateWorkspaceIncome,
        handleUpdateWorkspaceIncome,
        handleDeleteWorkspaceIncome,
    };
}
